import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { boxService } from '@/services/boxService';
import { projectService } from '@/services/projectService';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';

interface ImportSummary {
  associated: number;
  skipped: number;
  total_processed: number;
  errors: string[];
}

export function ImportBoxAssociationPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark } = useTheme();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

  // Fetch project
  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(Number(projectId)),
    enabled: !!projectId,
  });

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!projectId) throw new Error('Project ID is required');
      return boxService.importBoxAssociations(Number(projectId), file);
    },
    onSuccess: (data) => {
      setImportSummary(data.summary);
      setToastMessage(`Import réussi ! ${data.summary.associated} associations créées.`);
      setToastType('success');
      setShowToast(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Invalidate queries to refresh the data
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
        queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || 'Erreur lors de l\'import';
      setToastMessage(errorMessage);
      setToastType('error');
      setShowToast(true);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setToastMessage('Veuillez sélectionner un fichier CSV');
        setToastType('error');
        setShowToast(true);
        return;
      }
      setSelectedFile(file);
      setImportSummary(null);
    }
  };

  const handleImport = () => {
    if (!selectedFile) {
      setToastMessage('Veuillez sélectionner un fichier');
      setToastType('error');
      setShowToast(true);
      return;
    }
    importMutation.mutate(selectedFile);
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Project ID manquant</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Projet non trouvé</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/admin/projects/${projectId}/association`)}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
            >
              <span className="material-icons-outlined">arrow_back</span>
            </button>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Import Associations Box
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Instructions */}
            <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] p-6 mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="material-icons-outlined">info</span>
                Instructions d'import
              </h2>
              <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <p>Le fichier CSV doit contenir les colonnes suivantes :</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>bat</strong> : Numéro du bâtiment (ex: 30)</li>
                  <li><strong>etage</strong> : Nom de l'étage (ex: R+2)</li>
                  <li><strong>appart</strong> : Numéro de l'appartement (ex: 2105)</li>
                  <li><strong>box</strong> : Nom de la box à associer (ex: 30_R2_A)</li>
                </ul>
                <p className="mt-4">
                  <strong>Format attendu :</strong> bat,etage,appart,box
                </p>
                <div className="mt-4 p-3 bg-slate-50 dark:bg-[#0B1116] rounded-lg font-mono text-xs">
                  bat,etage,appart,box<br/>
                  30,R+2,2105,30_R2_A<br/>
                  30,R+5,2213,30_R5_E
                </div>
                <p className="mt-4 text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <span className="material-icons-outlined text-sm">warning</span>
                  Les zones (bâtiment, étage, appartement) et les boxs doivent déjà exister.
                </p>
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] p-6 mb-6">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Sélectionner le fichier CSV</h2>
              <div className="space-y-4">
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-slate-500 dark:text-slate-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary file:text-white
                      hover:file:bg-sky-600
                      file:cursor-pointer
                      dark:file:bg-primary dark:file:text-white"
                  />
                </div>
                {selectedFile && (
                  <div className="p-3 bg-slate-50 dark:bg-[#0B1116] rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="material-icons-outlined text-primary">description</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedFile.name}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleImport}
                  disabled={!selectedFile || importMutation.isPending}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {importMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <span className="material-icons-outlined">upload</span>
                      Importer
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Summary */}
            {importSummary && (
              <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="material-icons-outlined">check_circle</span>
                  Résumé de l'import
                </h2>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{importSummary.total_processed}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Lignes traitées</div>
                  </div>
                  <div className="p-4 bg-green-500/10 dark:bg-green-500/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {importSummary.associated}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Associations créées</div>
                  </div>
                  <div className="p-4 bg-amber-500/10 dark:bg-amber-500/20 rounded-lg">
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {importSummary.skipped}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Ignorées (déjà existantes)</div>
                  </div>
                </div>

                {importSummary.errors.length > 0 && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 max-h-60 overflow-y-auto">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-400 mb-2">Erreurs</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                      {importSummary.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={async () => {
                      if (projectId) {
                        await queryClient.invalidateQueries({ queryKey: ['boxes', projectId] });
                        await queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
                      }
                      navigate(`/admin/projects/${projectId}/association`);
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors flex items-center gap-2"
                  >
                    <span className="material-icons-outlined text-sm">visibility</span>
                    Voir les associations
                  </button>
                  <button
                    onClick={() => {
                      setImportSummary(null);
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Nouvel import
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}
